import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { DesignContext } from 'contexts/DesignContext'
import objectPath from 'object-path'
import { Modal } from 'react-bootstrap'
import Swal from 'sweetalert2'
import styles from 'designer/styles/modal.module.css'
import Component from './index'
import FormLoading from 'components/Loader/FormLoading'

import {
  getElementAction,
  updateCondition,
} from 'designer/component/action/Condition'
import {
  getForm,
  getFormDesign,
  getPage,
  loadApiToFormData,
  loadComponentData,
  loadDesignerData,
  loadUrlParamToFormData,
} from 'designer/component/action/Design'
import {
  compareTwoValue,
  generateString,
  getCurrentUi,
  setRequestFormData,
  updateFormDataValue,
} from '../action/Data'
import { isNumber } from '../action/Data'
import PatientAssessment from './PatientAssessment'
import cloneDeep from 'clone-deep'
import { onCheckCondition } from '../action/condition/Action'
import { checkPropTab, getApi } from '../action/Api'
import httpClient from 'utility/httpClient.util'
import Title from '../container/title/Title'
import {
  getFirstDefinedValue,
  runModalReceiveRules,
  updateModalReceiveFilesFormData,
} from './modalReceiveData.util'
import { cancelDebounceCallback } from 'utility/debounceCallback.util'

const dynamicInputAppendModalDesignRequests = new WeakMap()

const isEmptyModalValue = (value) =>
  value === undefined || value === null || value === ''

const addModalValueKey = (keys, key) => {
  if (!key) return

  String(key)
    .split('|')
    .map((item) => item.trim())
    .filter((item) => item && !keys.includes(item))
    .forEach((item) => keys.push(item))
}

const getModalInputValue = ({
  formData = {},
  designerData = {},
  searchData = {},
  item = {},
  ui = {},
}) => {
  let keys = []
  addModalValueKey(keys, item.systemKey)
  addModalValueKey(keys, ui.name)
  addModalValueKey(keys, item.fullPropKey)
  addModalValueKey(keys, item.prop)
  addModalValueKey(keys, item.dynamicName)

  if (Array.isArray(item.system)) {
    item.system.forEach(({ field, systemKey, systemName, dynamicName }) => {
      addModalValueKey(keys, field)
      addModalValueKey(keys, systemKey)
      addModalValueKey(keys, systemName)
      addModalValueKey(keys, dynamicName)
    })
  }

  if (keys.includes('hn')) {
    addModalValueKey(keys, 'hnSelect')
  }

  let source = [formData, designerData, searchData].find((data = {}) => {
    return keys.find((key) => {
      if (!objectPath.has(data, key)) return false

      let value = objectPath.get(data, key)
      return !isEmptyModalValue(value)
    })
  })

  if (!source) return undefined

  let key = keys.find((key) => {
    if (!objectPath.has(source, key)) return false

    let value = objectPath.get(source, key)
    return !isEmptyModalValue(value)
  })

  return objectPath.get(source, key)
}

const NormalModal = ({
  children,
  isOpenModal,
  isRuleOpenModal,
  isModalCondition,
  closeExternalModal,
  ui,
  modalAction,
}) => {
  const modalSize = modalAction?.modalSize || ui?.modalSize || 'xl'
  const cardColor = modalAction?.cardColor || ui.cardColor
  const textColor = modalAction?.textColor || ui.textColor
  const fontSize = modalAction?.fontSize || ui.fontWeight || '18px'
  const fontWeight = modalAction?.fontWeight || ui.fontWeight || 'bold'
  const title = modalAction?.title || ui.title
  const closeButton = modalAction?.closeButton
  const isActionModalTitle = isRuleOpenModal || isModalCondition
  const titleUischema = isActionModalTitle
    ? { ...ui, ...modalAction, fontWeight, fontSize }
    : { ...modalAction, ...ui, fontWeight, fontSize }
  let props = {
    uischema: titleUischema,
    isModalTitle: true,
    isActive: false,
    modalTitle: title,
  }

  return (
    <Modal show={isOpenModal} size={modalSize}>
      <Modal.Header
        closeButton={closeButton === undefined ? true : closeButton}
        onHide={closeExternalModal}
        style={{
          backgroundColor: cardColor || '#fff',
        }}
      >
        <h5
          className="mb-0"
          style={{
            color: textColor || '#000',
          }}
        >
          <Title {...props} />
        </h5>
      </Modal.Header>
      <Modal.Body className="p-0">{children}</Modal.Body>
    </Modal>
  )
}

const FullSizeModal = ({
  children,
  isOpenModal,
  isRuleOpenModal,
  isModalCondition,
  closeExternalModal,
  ui,
  modalAction,
}) => {
  const cardColor = modalAction?.cardColor || ui.cardColor
  const textColor = modalAction?.textColor || ui.textColor
  const fontSize = modalAction?.fontSize || ui.fontWeight || '18px'
  const fontWeight = modalAction?.fontWeight || ui.fontWeight || 'bold'
  const title = modalAction?.title || ui.title
  const closeButton = modalAction?.closeButton
  const isActionModalTitle = isRuleOpenModal || isModalCondition
  const titleUischema = isActionModalTitle
    ? { ...ui, ...modalAction, fontWeight, fontSize }
    : { ...modalAction, ...ui, fontWeight, fontSize }
  let props = {
    uischema: titleUischema,
    isModalTitle: true,
    isActive: false,
    modalTitle: title,
  }

  return (
    <Modal
      fullscreen="true"
      dialogClassName={styles.modalFullScreen}
      className="pl-0"
      show={isOpenModal}
    >
      <Modal.Header
        closeButton={closeButton === undefined ? true : closeButton}
        onHide={closeExternalModal}
        style={{
          backgroundColor: cardColor || '#fff',
        }}
      >
        <h5
          className="mb-0"
          style={{
            color: textColor || '#000',
          }}
        >
          <Title {...props} />
        </h5>
      </Modal.Header>
      <Modal.Body className="p-0">{children}</Modal.Body>
    </Modal>
  )
}

const hasModalDataValue = (value) => {
  if (value === undefined || value === null || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

const getModalDataValue = (data, keys = []) => {
  for (let key of keys) {
    if (key && objectPath.has(data, key)) {
      return objectPath.get(data, key)
    }
  }
  return undefined
}

const createComingTableFromModalData = (data = {}, replaceTable = []) => {
  if (!replaceTable || replaceTable.length === 0) return []

  let rowData = {}
  replaceTable.forEach(({ response, value }) => {
    let rowValue = getModalDataValue(data, [response, value])
    if (rowValue !== undefined) {
      if (response) objectPath.set(rowData, response, rowValue)
      if (value) objectPath.set(rowData, value, rowValue)
    }
  })
  let directDataKeys = ['itemCode', 'unitPrice']
  directDataKeys.forEach((key) => {
    let rowValue = getModalDataValue(data, [key])
    if (rowValue !== undefined && !objectPath.has(rowData, key)) {
      objectPath.set(rowData, key, rowValue)
    }
  })

  let hasValue = Object.values(rowData).some((value) =>
    hasModalDataValue(value),
  )
  return hasValue ? [rowData] : []
}

const addUniqueKey = (keys = [], key = '') => {
  if (!key || keys.includes(key)) return keys

  keys.push(key)
  return keys
}

const getInputAppendSearchKeys = (jsonForm = {}, ui = {}) => {
  let keys = []
  addUniqueKey(keys, ui.name)

  let searchFields = objectPath.get(jsonForm, 'search.field') || []
  searchFields.forEach(({ key, name }) => {
    let isCurrentField =
      key === ui.key || key === ui.name || name === ui.key || name === ui.name
    if (!isCurrentField) return

    addUniqueKey(keys, name)
    addUniqueKey(keys, key)
  })

  addUniqueKey(keys, ui.key)
  return keys
}

const getFirstDataValue = (data = {}, keys = []) => {
  let value
  keys.find((key) => {
    let currentValue = objectPath.get(data, key)
    if (!hasModalDataValue(currentValue)) return false

    value = currentValue
    return true
  })

  return value
}

const createInputAppendSearchData = ({
  jsonForm = {},
  ui = {},
  componentData = {},
  formData = {},
  searchData = {},
}) => {
  let searchKeys = getInputAppendSearchKeys(jsonForm, ui)
  let value = getFirstDataValue(componentData, searchKeys)
  if (!hasModalDataValue(value)) {
    value = getFirstDataValue(formData, searchKeys)
  }
  if (!hasModalDataValue(value)) {
    value = getFirstDataValue(searchData, searchKeys)
  }

  if (!hasModalDataValue(value)) return null

  let nextSearchData = cloneDeep(searchData || {})
  searchKeys.forEach((key) => objectPath.set(nextSearchData, key, value))

  return { keys: searchKeys, searchData: nextSearchData, value }
}

const ModalComponent = (props) => {
  const {
    ui = {},
    modalAction,
    isOpenModal = false,
    onRefreshTable,
    designerPropsData = {},
    isModalCondition,
    closeModalCondition,
    uiModalCode,
    modalDynamicIndex = null,
    modalOpenSource = '',
    dynamicData,
    dynamicKey,
    dynamicName,
  } = props

  const designContext = useContext(DesignContext)
  const datasourceValue = objectPath.get(designContext, `@datasource`) || {}

  const {
    isPage,
    jsonForm,
    setDesignerPropsData,
    setComponentPropsData,
    openModal,
    setOpenModal,
    setModalComponent,
    setSchema,
    setFormData,
    searchData,
    setSearchData,
    showInvisible,
    setIsInitialOption,
    tabParamActive,
    setOpenModalCondition,
    isUiChanged,
    setIsUiChanged,
    setIsLoadingApi,
    widgetDesign,
  } = designContext

  const modalId = objectPath.get(modalAction, 'formId')
  const componentKey = ui.key
  const checkDup = ui.checkDup
  const checkDupKey = ui.checkDupKey
  const alertMessage = ui.alertMessage
  const modalSize = modalAction?.modalSize || ui?.modalSize || 'xl'
  const modalType = modalAction?.modalType || ''
  const sourceType =
    modalAction?.sourceType ||
    objectPath.get(jsonForm, 'designerPropsData.sourceType') ||
    ''
  const modalCode = objectPath.get(jsonForm, 'designerPropsData.modal')
  const isInputAppendModal = modalOpenSource === 'inputAppend'
  const isDynamicInputAppendModal = isInputAppendModal && Boolean(dynamicKey)
  const modalConditionCode = objectPath.get(isUiChanged, 'modal.ui.key')
  const isOpening = objectPath.get(isUiChanged, 'modal.isOpening')
  const isCheckingDesignCode = objectPath.get(
    isUiChanged,
    'isCheckingDesignCode',
  )

  const getModalDesign = (code) => {
    if (!isDynamicInputAppendModal) return getFormDesign(code, false)

    let modalRequestKey = objectPath.get(isUiChanged, ['modal', code])
    if (!modalRequestKey) return getFormDesign(code, false)

    let modalDesignRequest = dynamicInputAppendModalDesignRequests.get(
      modalRequestKey,
    )
    if (!modalDesignRequest) {
      modalDesignRequest = getFormDesign(code, false)
      dynamicInputAppendModalDesignRequests.set(
        modalRequestKey,
        modalDesignRequest,
      )
    }
    return modalDesignRequest
  }

  const match = useRouteMatch()
  const location = useLocation()
  const search = location.search || []
  const receivePathParams = match.params
  const urlSearchParams = new URLSearchParams(search)
  const receiveQueryParams = Object.fromEntries([...urlSearchParams])
  const allParams = { ...receivePathParams, ...receiveQueryParams }

  const [modal, setModal] = useState({})
  const [modalLoadError, setModalLoadError] = useState('')
  const [modalLoadStatus, setModalLoadStatus] = useState('idle')
  const modalLoadRequestRef = useRef(0)

  const isReadyModalDesign = (designJsonForm = {}) => {
    let schema = objectPath.get(designJsonForm, 'schema') || {}
    let uischema = objectPath.get(designJsonForm, 'uischema') || {}

    return Object.keys(schema).length > 0 && Object.keys(uischema).length > 0
  }

  const setModalLoadFail = () => {
    setModal({})
    setModalLoadError('โหลดข้อมูลแจ้งเตือนไม่สำเร็จ')
    setModalLoadStatus('error')
  }

  const closeExternalModal = (componentPropsData = {}, receiveProps = []) => {
    setIsLoadingApi('done')
    if (isModalCondition) {
      if (
        !modalCode ||
        !uiModalCode ||
        (modalCode && uiModalCode && modalCode !== uiModalCode)
      )
        return
    } else {
      if (!modalCode) return
    }
    let conditions = objectPath.get(jsonForm, 'conditions')
    let formData = objectPath.get(jsonForm, 'formData') || {}
    let newSearchData = { ...searchData, ...formData }
    let elementAction = getElementAction(designContext, conditions, jsonForm)
    let tagname = ui.tagname
    let isDynamic = tagname === 'dynamic'
    if (dynamicKey) {
      objectPath.del(isUiChanged, [ui.key, 'dynamicModal'])
      if (!isDynamicInputAppendModal) {
        objectPath.set(isUiChanged, [dynamicKey, 'isRefreshDynamic'], true)
      }
    }

    let { isRefreshTable = false } = componentPropsData

    if (objectPath.get(formData, '@condition') === undefined) {
      objectPath.set(formData, `@condition`, {})
    }
    objectPath.set(formData, `@condition.${modalId}`, {})

    objectPath.set(designerPropsData, 'modal', null)
    setDesignerPropsData(designerPropsData)
    setOpenModal(null)
    let modalCondition = cloneDeep(modal?.ui?.jsonForm?.conditions)

    setModal({})
    setModalLoadError('')
    setModalLoadStatus('idle')

    let actionUpdate = objectPath.get(elementAction, ['actionUpdate'])
    if (actionUpdate) {
      actionUpdate.forEach(async (key) => {
        formData = await updateCondition(
          showInvisible,
          formData,
          key,
          designContext,
        )
        if (isPage === 1) {
          newSearchData = await updateCondition(
            showInvisible,
            newSearchData,
            key,
            designContext,
          )
        }
      })
    }
    let designerData = objectPath.get(jsonForm, 'designerPropsData.data') || {}
    let componentData = objectPath.get(componentPropsData, 'data') || {}
    let data = { ...designerData, ...componentData }
    let formGroup = objectPath.get(jsonForm, 'formGroup')

    if (componentPropsData.receive) {
      receiveProps = componentPropsData.receive || []
    }
    let sendProps = componentPropsData.send
    let inputAppendSearch = null

    let filterReceive
    if (sendProps) {
      filterReceive = receiveProps.filter((receive) => {
        let found = sendProps.find((send) => {
          let fullSendKey = send.prop
          if (send.jsonKey) {
            fullSendKey = `${fullSendKey}.${send.jsonKey}`
          }
          return fullSendKey === receive.fullPropKey
        })
        return found ? true : false
      })
    } else {
      filterReceive = receiveProps
    }

    filterReceive.forEach((itemReceive) => {
      let {
        prop,
        system = [],
        systemType,
        propKey,
        jsonKey,
        fullPropKey,
        systemName,
        systemKey,
        isDynamic,
        propType,
      } = itemReceive
      let label, name
      let value
      let foundSend =
        sendProps && sendProps.find((send) => send.prop === itemReceive.prop)
      if (itemReceive.propType === 'custom') {
        if (foundSend) {
          value = foundSend.value
        }
      } else {
        value = getFirstDefinedValue(data, [
          foundSend?.value,
          fullPropKey ? `@sendData.${fullPropKey}` : '',
          fullPropKey,
          jsonKey ? `@sendData.${jsonKey}` : '',
          jsonKey,
          propKey ? `@sendData.${propKey}` : '',
          propKey,
          prop ? `@sendData.${prop}` : '',
          prop,
        ])
      }
      if (systemName) {
        let nameSplit = systemName.split(' | ')
        label = objectPath.get(nameSplit, 0)
        name = objectPath.get(nameSplit, 1)
      }
      if (isDynamic) {
        if (systemType === 'input') {
          let value = objectPath.get(data, `${fullPropKey || jsonKey}`)
          system.forEach((item) => {
            let fullKey = `${dynamicName}.${modalDynamicIndex}.${item.dynamicName}`
            objectPath.set(formData, fullKey, value)
            if (isPage === 1) {
              objectPath.set(newSearchData, fullKey, value)
            }
          })
        } else {
          system.forEach(({ field, systemKey, systemName, dynamicName }) => {
            let value = objectPath.get(data, field)
            objectPath.set(formData, [dynamicName, systemKey], value)
            if (isPage === 1) {
              objectPath.set(newSearchData, [dynamicName, systemKey], value)
            }
          })
        }
      } else if (systemType === 'tableWaitUpsert') {
        let dataTable = objectPath.get(formData, systemKey) || []
        let rowData = objectPath.get(data, prop)
        objectPath.set(isUiChanged, 'isWaitUpsert', {
          status: true,
          key: systemKey,
          table: dataTable,
          rowData: rowData,
          isModalProp: true,
          isReplace: false,
        })
      } else if (systemType === 'tableConfirmUpsert') {
        let dataTable = objectPath.get(componentData, fullPropKey) || []
        objectPath.set(formData, systemKey, dataTable)
      } else if (systemType === 'addToTable') {
        let currentTable = objectPath.get(formData, systemKey) || []
        currentTable = Array.isArray(currentTable) ? currentTable : []
        let comingTable = Array.isArray(value) ? value : value ? [value] : []
        let replaceTable = itemReceive?.dataTable
        let tableValueType = itemReceive?.tableValueType
        if (
          replaceTable &&
          tableValueType !== 'arrayField' &&
          comingTable.length === 0
        ) {
          comingTable = createComingTableFromModalData(data, replaceTable)
        }
        if (replaceTable && tableValueType === 'arrayField') {
          let arrayTable = []
          comingTable.forEach((coming) => {
            replaceTable.forEach((replace) => {
              let { arrayField, subResponse = [] } = replace
              let hasValue = objectPath.has(coming, arrayField)
              if (hasValue) {
                let value = objectPath.get(coming, arrayField)
                if (
                  Object.prototype.toString.call(value) === '[object Object]'
                ) {
                  value = [value]
                }
                if (Array.isArray(value)) {
                  value.forEach((rowValue, index) => {
                    let arrayRow = {}
                    subResponse.forEach((subItem) => {
                      let replaceKey = subItem.value
                      let comingKey = subItem.response
                      if (replaceKey && comingKey) {
                        let fieldList = replaceKey.split('.#.')
                        let subValue = rowValue
                        fieldList.forEach((subField) => {
                          if (Array.isArray(subValue)) {
                            let newSubValue = []
                            subValue.forEach((value) => {
                              let subValue = objectPath.get(value, subField)
                              newSubValue.push(subValue)
                            })
                            subValue = newSubValue
                          } else {
                            subValue = objectPath.get(subValue, subField)
                          }
                        })
                        objectPath.set(arrayRow, comingKey, subValue)
                      }
                    })
                    arrayTable.push(arrayRow)
                  })
                }
              }
            })
          })
          comingTable = arrayTable
        } else if (replaceTable && comingTable.length > 0) {
          comingTable.map((coming) => {
            replaceTable.forEach((replace) => {
              let hasValue = objectPath.has(coming, replace.response)
              if (hasValue) {
                let value = objectPath.get(coming, replace.response)
                objectPath.set(coming, replace.value, value)
              }
            })
            return coming
          })
        }
        let columnFilter = itemReceive?.columnFilter
        if (columnFilter && comingTable.length > 0) {
          comingTable = comingTable.filter((coming) => {
            let foundFilter = columnFilter.every((item) => {
              let { compareType, key, filterValue } = item
              let rowValue = objectPath.get(coming, item.value)
              return compareTwoValue(compareType, rowValue, filterValue)
            })
            return foundFilter
          })
        }
        console.log('columnFilter', { itemReceive, columnFilter, comingTable })
        let newTable = currentTable.concat(comingTable)
        system.forEach(({ systemKey, dynamicName }) => {
          objectPath.set(formData, systemKey, newTable)
          objectPath.set(formData, dynamicName, newTable)
        })
      } else if (systemType === 'replaceRow') {
        let tableUi = getCurrentUi(
          systemKey,
          jsonForm.schema,
          jsonForm.uischema,
        )
        let columnRefId
        if (tableUi.key === systemKey) {
          columnRefId = tableUi?.loadData?.columnRefId
        }
        let currentTable = objectPath.get(formData, system) || []
        let currentData =
          value ||
          objectPath.get(designerData, [systemKey, itemReceive.fullPropKey]) ||
          []

        let currentIndex = currentTable.findIndex((item) => {
          if (item['@genId']) {
            return (
              objectPath.get(item, '@genId') ===
              objectPath.get(currentData, '@genId')
            )
          }
          return (
            objectPath.get(item, columnRefId) ===
              objectPath.get(currentData, columnRefId) ||
            item?.tableData?.no === currentData?.tableData?.no
          )
        })
        if (currentIndex > -1) {
          currentTable[currentIndex] = { ...currentData, ...value }
        }
        objectPath.set(formData, systemKey, currentTable)
        objectPath.set(formData, system, currentTable)
      } else if (systemType === 'rowWaitReplace') {
        let currentTable = cloneDeep(objectPath.get(formData, systemKey)) || []
        let rowData = cloneDeep(objectPath.get(data, prop))
        let rowEdit = objectPath.get(isUiChanged, '@rowEdit')
        let replaceTable = []
        currentTable.forEach((item) => {
          if (item.tableData.no === rowEdit.tableData.no) {
            objectPath.del(rowData, 'tableData')
            item = {
              ...item,
              ...rowData,
            }
          }
          replaceTable.push(item)
        })
        objectPath.set(isUiChanged, 'isWaitUpsert', {
          status: true,
          key: systemKey,
          table: replaceTable,
          rowData: [rowData],
          isModalProp: true,
          isReplace: true,
        })
      } else if (systemType === 'rowConfirmReplace') {
      } else if (systemType === 'table') {
        if (propType === 'formData') {
          let currentTable = objectPath.get(formData, ui.key) || []
          let rowTableValue = objectPath.get(data, fullPropKey) || data || {}
          let columnRefId = ui?.loadData?.columnRefId
          if (columnRefId) {
            let id = objectPath.get(rowTableValue, columnRefId)
            let genId = objectPath.get(rowTableValue, '@genId')
            if (id || genId) {
              let foundIndex = currentTable.findIndex((item) => {
                let itemId = objectPath.get(item, columnRefId)
                if (itemId && id && itemId === id) {
                  return true
                }
                let itemGenId = objectPath.get(item, '@genId')
                return itemGenId === genId
              })
              if (foundIndex > -1) {
                objectPath.set(currentTable, foundIndex, rowTableValue)
                console.log('currentTable', { id, genId, currentTable })
              }
            } else {
              let genId = generateString(10)
              objectPath.set(rowTableValue, '@genId', genId)
              currentTable.push(rowTableValue)
            }
            objectPath.set(formData, ui.name, currentTable)
            objectPath.set(formData, ui.key, currentTable)
          }
        } else {
          let tableValue = objectPath.get(data, `${systemKey}.${prop}`)
          objectPath.set(formData, prop, tableValue)
        }
      } else if (systemType === 'input') {
        objectPath.set(formData, systemKey, value)
        if (name) {
          objectPath.set(formData, name, value)
        }
        if (isPage === 1) {
          objectPath.set(newSearchData, systemKey, value)
          if (name) {
            objectPath.set(newSearchData, name, value)
          }
        }
      } else if (systemType === 'groupInput') {
        system.forEach(({ field, systemKey, systemName }) => {
          let value = objectPath.get(data, field)
          objectPath.set(formData, systemKey, value)
          if (isPage === 1) {
            objectPath.set(newSearchData, systemKey, value)
            objectPath.set(newSearchData, systemName, value)
          }
        })
      } else if (systemType === 'formData') {
        let source = getFirstDefinedValue(data, [
          propKey ? `@sendData.${propKey}` : '',
          propKey,
          prop ? `@sendData.${prop}` : '',
          prop,
        ])
        formData = updateFormDataValue({
          jsonForm,
          formGroup: formGroup,
          formDataName: systemName,
          formDataKey: systemKey,
          source: source,
          destination: formData,
          type: 'prop',
          isRequired: false,
        })
        if (isPage === 1) {
          newSearchData = updateFormDataValue({
            jsonForm,
            formGroup: formGroup,
            formDataName: systemName,
            formDataKey: systemKey,
            source: source,
            destination: formData,
            type: 'prop',
            isRequired: false,
          })
        }
      } else if (systemType === 'widget') {
        let source = getFirstDefinedValue(data, [
          propKey ? `@sendData.${propKey}` : '',
          propKey,
          prop ? `@sendData.${prop}` : '',
          prop,
        ])
        objectPath.set(formData, systemKey, source)
        objectPath.set(formData, systemName, source)
      } else if (systemType === 'condition') {
        let conditionValue = 1
        objectPath.set(
          formData,
          `@condition.${modalId}.${fullPropKey}`,
          conditionValue,
        )
        if (isPage === 1) {
          objectPath.set(
            newSearchData,
            `@condition.${modalId}.${fullPropKey}`,
            conditionValue,
          )
          objectPath.set(
            formData,
            `@condition.${modalId}.${fullPropKey}`,
            conditionValue,
          )
        }
      } else if (systemType === 'dynamic') {
        let data = objectPath.get(componentPropsData, 'data')
        let value = objectPath.get(data, prop)
        if (value) {
          if (modalDynamicIndex > -1) {
            objectPath.set(formData, [systemKey, modalDynamicIndex], value)
            objectPath.set(formData, [name, modalDynamicIndex], value)
            if (isPage === 1) {
              objectPath.set(
                newSearchData,
                [systemKey, modalDynamicIndex],
                value,
              )
              objectPath.set(newSearchData, [name, modalDynamicIndex], value)
            }
          } else {
            let isPassDup = true
            if (checkDup) {
              let currentData = objectPath.get(formData, name)
              let foundData = currentData.find((item) => {
                return (
                  objectPath.get(item, checkDupKey) ===
                  objectPath.get(value, checkDupKey)
                )
              })
              if (foundData) {
                isPassDup = false
                Swal.fire({
                  icon: 'error',
                  title: alertMessage || 'พบรายการซ้ำ',
                  text: 'กรุณาลองใหม่อีกครั้ง',
                  confirmButtonText: 'ปิด',
                })
              }
            }
            if (isPassDup) {
              if (name) {
                objectPath.push(formData, name, value)
              } else {
                objectPath.push(formData, systemKey, value)
              }
              if (isPage === 1) {
                if (name) {
                  objectPath.push(newSearchData, name, value)
                } else {
                  objectPath.push(newSearchData, systemKey, value)
                }
              }
            }
          }
        }
      } else if (systemType === 'buttonAction') {
        objectPath.set(isUiChanged, itemReceive.elementKey, { doAction: true })
      } else if (systemType === 'files') {
        let result = updateModalReceiveFilesFormData({
          formData,
          newSearchData,
          data,
          prop,
          propKey,
          fullPropKey,
          systemKey,
          isPage,
        })
        formData = result.formData
        newSearchData = result.newSearchData
      } else if (systemType === 'addFiles') {
        let oldFiles = objectPath.get(formData, systemKey) || []
        let newFiles = objectPath.get(data, propKey)
        let concatFiles = newFiles.concat(oldFiles)
        objectPath.set(formData, systemKey, concatFiles)
        objectPath.set(formData, prop, concatFiles)
      } else {
        let value = objectPath.get(data, prop)
        objectPath.set(formData, prop, value)
        if (isPage === 1) {
          objectPath.set(newSearchData, prop, value)
        }
      }
    })

    if (sendProps) {
      sendProps.forEach(({ prop, propType, source }) => {
        if (propType === 'condition') {
          let conditionValue = 1
          objectPath.set(
            formData,
            `@condition.${modalId}.${prop}`,
            conditionValue,
          )
          if (isPage === 1) {
            objectPath.set(
              newSearchData,
              `@condition.${modalId}.${prop}`,
              conditionValue,
            )
            objectPath.set(
              formData,
              `@condition.${modalId}.${prop}`,
              conditionValue,
            )
          }
        }
      })
    }
    let isResetAll = false
    if (!isInputAppendModal) {
      onCheckCondition(designContext, formData, null, {
        isCloseModal: true,
        isModalCondition: isModalCondition,
        modalId: ui?.action?.formId,
        modalCondition: modalCondition,
      })
      isResetAll = objectPath.get(isUiChanged, 'isResetAll')
      formData = objectPath.get(jsonForm, 'formData') || formData
      if (isResetAll) {
        newSearchData = {}
      } else {
        formData = runModalReceiveRules({
          designContext,
          formData,
          receiveProps: filterReceive,
          status: {
            modalId: ui?.action?.formId,
            modalCondition: modalCondition,
            isModalCondition: isModalCondition,
          },
        })
      }
    }
    if (
      isInputAppendModal &&
      !isDynamicInputAppendModal &&
      isPage === 1 &&
      hasModalDataValue(componentData)
    ) {
      inputAppendSearch = createInputAppendSearchData({
        jsonForm,
        ui,
        componentData,
        formData,
        searchData: newSearchData,
      })
      if (inputAppendSearch) {
        inputAppendSearch.keys.forEach((key) =>
          objectPath.set(formData, key, inputAppendSearch.value),
        )
        newSearchData = inputAppendSearch.searchData
      }
    }
    // objectPath.set(formData, '@condition', {})
    setFormData(formData)
    if (isPage === 1 && !isDynamicInputAppendModal) {
      setSearchData(newSearchData)
    }
    if (inputAppendSearch) {
      cancelDebounceCallback('onInputUpdate')
      objectPath.set(isUiChanged, 'fetchSearch', inputAppendSearch.searchData)
      objectPath.set(isUiChanged, 'onChange', true)
    }
    if (!isInputAppendModal) {
      setIsInitialOption(-1)
      objectPath.del(isUiChanged, 'fetchSearch')
      objectPath.del(isUiChanged, 'tableSelection')
      objectPath.set(isUiChanged, 'isRefreshTable', true)
    }

    if (isOpening) {
      objectPath.set(isUiChanged, 'modal.isOpening', false)
    }

    setIsUiChanged(isUiChanged)
    if (isModalCondition) {
      closeModalCondition()
    }
    if (isRefreshTable && !isInputAppendModal) {
      if (onRefreshTable) {
        onRefreshTable()
      } else {
        setComponentPropsData({ isRefreshTable: isRefreshTable })
      }
    }
  }

  const onCancelAssessment = () => {
    setOpenModal(null)
    setModal({})
    setModalLoadError('')
    setModalLoadStatus('idle')
  }

  useEffect(async () => {
    const modalLoadRequest = modalLoadRequestRef.current + 1
    modalLoadRequestRef.current = modalLoadRequest
    if (modalConditionCode && isOpening) return
    if (isOpening && modalCode === isCheckingDesignCode) return
    let modalKey = ui.key
    if (!isOpenModal) return
    if (openModal !== modalKey) {
      if (isModalCondition) {
        return
      }
      return closeExternalModal()
    }
    setModal({})
    setModalLoadError('')
    setModalLoadStatus('loading')
    let design = {}
    try {
      if (isModalCondition) {
        if (!uiModalCode) {
          setModalLoadFail()
        } else if (sourceType === 'form') {
          design = await getModalDesign(uiModalCode)
        } else if (sourceType === 'page') {
          design = await getModalDesign(uiModalCode)
        }
      } else {
        if (!modalCode) {
          setModalLoadFail()
        } else if (sourceType === 'form') {
          design = await getModalDesign(modalCode)
        } else if (sourceType === 'page') {
          design = await getModalDesign(modalCode)
        }
      }
      if (modalLoadRequestRef.current !== modalLoadRequest) return
      let designJsonForm = cloneDeep(design.jsonForm)
      if (isReadyModalDesign(designJsonForm)) {
        objectPath.set(designJsonForm, 'componentPropsData', {})
        let modalMapFormData =
          objectPath.get(designJsonForm, 'schema.mapFormData') || {}
        let modalMapName =
          objectPath.get(designJsonForm, 'schema.mapName') || {}
        let modalMapField =
          objectPath.get(designJsonForm, 'schema.mapField') || {}
        let modalMapTag = objectPath.get(designJsonForm, 'schema.mapTag') || {}
        let filterModalFormData = Object.keys(modalMapFormData).filter(
          (key) => modalMapFormData[key],
        )
        let schema = jsonForm.schema
        objectPath.set(schema, 'mapModal', {})
        filterModalFormData.forEach((key) => {
          let name = modalMapName[key].split('.').pop()
          objectPath.set(schema, `mapModal.name.${key}`, `modal.${name}`)
          objectPath.set(schema, `mapModal.field.${key}`, modalMapField[key])
          objectPath.set(schema, `mapModal.tag.${key}`, modalMapTag[key])
        })
        setSchema(schema)

        let modal = cloneDeep(ui)
        objectPath.set(modal, 'ui.close', false)
        objectPath.set(modal, 'ui.formDesign', design.formDesign)

        let designerProps = objectPath.get(designerPropsData, 'props') || []
        let receiveProps = objectPath.get(modalAction, 'props') || []
        let designerData = objectPath.get(designerPropsData, 'data')
        let allFormGroup = jsonForm.formGroup
        receiveProps =
          typeof receiveProps === 'object'
            ? Object.values(receiveProps)
            : receiveProps

        let systemData = {}
        const receivePropRequests = receiveProps.map(async (item) => {
          if (!item.propReceive) return
          let value
          let systemKey = item?.systemKey || ''
          let foundDesignerProp = designerProps.find(
            ({ key }) => systemKey === key,
          )
          if (foundDesignerProp) {
            item.system = foundDesignerProp.system
          }
          if (item.systemType === 'tab') {
            let [containerKey, paramName] = systemKey.split('.')
            value = checkPropTab({ designContext }, { containerKey, paramName })
            objectPath.set(systemData, item.fullPropKey, value)
          } else if (item.systemType === 'dynamic') {
            if (dynamicKey === systemKey) {
              if (item.jsonKey) {
                value = objectPath.get(
                  dynamicData,
                  `${modalDynamicIndex}.${item.jsonKey}`,
                )
              } else {
                value = dynamicData
              }
            } else {
              if (item.jsonKey) {
                let dynamicData = objectPath.get(
                  jsonForm.formData,
                  `${systemKey}.${modalDynamicIndex}`,
                )
                value = objectPath.get(dynamicData, item.jsonKey)
              } else {
                let dynamicData = objectPath.get(jsonForm.formData, systemKey)
                value = dynamicData
              }
            }
          } else if (item.systemType === 'datasource') {
            value = objectPath.get(datasourceValue, systemKey)
          } else if (item.systemType === 'formData') {
            let form = allFormGroup.find(({ key }) => key === systemKey)
            value = setRequestFormData(
              form,
              {},
              {
                datasourceValue,
                formData: jsonForm.formData,
                allParams,
                jsonForm,
              },
            )
          } else if (item.systemType === 'table') {
            value = objectPath.get(
              designerData,
              `${systemKey}.${item.fullPropKey}`,
            )
            objectPath.set(isUiChanged, '@rowEdit', value)
            if (item.selectedRow) {
              objectPath.set(isUiChanged, ['selectedRow', item.propKey], value)
            }
          } else if (item.systemType === 'replaceRow') {
          } else if (item.systemType === 'condition') {
            value = 1
          } else if (item.propType === 'system') {
          } else if (item.systemType === 'custom') {
            value = item.systemValue
          } else if (item.systemType === 'tableResponse') {
            let tableKey = item.systemKey
            let tableUi = getCurrentUi(
              tableKey,
              jsonForm.schema,
              jsonForm.uischema,
            )
            if (tableUi?.loadData?.method === 'GET') {
              let action = tableUi?.loadData?.get
              let dataKey = action.data
              let requestUrl = getApi(
                action,
                {
                  designContext,
                },
                'Modal',
              )
              let response = await httpClient
                .get(requestUrl)
                .then(({ data }) => data)
                .catch(() => [])
              value = dataKey ? objectPath.get(response, dataKey) : response
            } else {
              value = objectPath.get(jsonForm.formData, item.systemKey)
            }
          } else if (item.propReceive && item.systemType === 'input') {
            let ui = getCurrentUi(
              item.systemKey,
              jsonForm.schema,
              jsonForm.uischema,
            )
            if (item.systemKey === ui.key) {
              value =
                objectPath.get(jsonForm.formData, item.systemKey) ||
                objectPath.get(jsonForm.formData, ui.name)
            } else {
              value = objectPath.get(jsonForm.formData, item.systemKey)
            }
            if (isEmptyModalValue(value)) {
              value = getModalInputValue({
                formData: jsonForm.formData,
                designerData,
                searchData,
                item,
                ui,
              })
            }
          } else if (item.systemType === 'apiResponse') {
            value = objectPath.get(
              jsonForm.formData,
              `@response.${item.systemKey}`,
            )
          } else if (item.systemType === 'tableWaitUpsert') {
            let isWaitUpsert = objectPath.get(isUiChanged, 'isWaitUpsert')
            value = isWaitUpsert?.rowData
          } else if (item.systemType === 'tableConfirmUpsert') {
            let isWaitUpsert = objectPath.get(isUiChanged, 'isWaitUpsert')
            value = isWaitUpsert?.table
          } else {
            value =
              objectPath.get(designerData, item.fullPropKey) ||
              objectPath.get(jsonForm.formData, item.fullPropKey)
          }
          if (
            item.propType === 'datasource' ||
            item.systemType === 'datasource'
          ) {
            objectPath.set(
              datasourceValue,
              `receiveProps.${item.fullPropKey}`,
              value,
            )
          }
          objectPath.set(systemData, item.fullPropKey, value)
          return item
        })
        await Promise.all(receivePropRequests)
        if (modalLoadRequestRef.current !== modalLoadRequest) return

        let formData = objectPath.get(designJsonForm, 'formData')
        if (isModalCondition) {
          let jsonForm = designJsonForm
          let formGroup = objectPath.get(jsonForm, 'formGroup')
          let apiGroup = objectPath.get(jsonForm, 'apiGroup')
          const componentPropsData = jsonForm?.componentPropsData || {}
          const componentData = objectPath.get(componentPropsData, 'data')
          formData = loadDesignerData(
            receiveProps,
            { jsonForm, designerData, formData, datasourceValue, systemData },
            formGroup,
            modalDynamicIndex,
          )
          formData = loadComponentData(
            receiveProps,
            {
              jsonForm,
              componentData,
              designerData,
              formData,
              datasourceValue,
              systemData,
            },
            formGroup,
          )
          formData = loadUrlParamToFormData(
            jsonForm,
            formData,
            receivePathParams,
            receiveQueryParams,
          )
          formData = await loadApiToFormData(
            jsonForm,
            formData,
            { urlParams: allParams, designerData, datasourceValue },
            apiGroup,
          )
          objectPath.set(designJsonForm, 'designerPropsData.modal', uiModalCode)
        } else {
          objectPath.set(designJsonForm, 'designerPropsData.modal', modalCode)
        }
        objectPath.set(
          designJsonForm,
          'designerPropsData.receive',
          receiveProps,
        )
        objectPath.set(designJsonForm, 'designerPropsData.data', systemData)

        let hasReceiveProps = receiveProps.find(({ propReceive }) => propReceive)
        let newForm = {
          ...(hasReceiveProps ? designerData : {}),
          ...formData,
        }
        objectPath.set(jsonForm, 'formData', newForm)
        objectPath.set(modal, 'formData', newForm)
        objectPath.set(modal, 'ui.systemData', systemData)
        objectPath.set(modal, 'ui.jsonForm', designJsonForm)
        objectPath.set(modal, 'ui.receiveProps', receiveProps)
        objectPath.set(modal, 'ui.datasourceValue', datasourceValue)
        setModalComponent(componentKey, designJsonForm, true)
        setModal(modal)
        setModalLoadStatus('ready')
        setIsUiChanged(isUiChanged)
        console.log('modal designer', {
          newForm,
          isUiChanged,
          receiveProps,
          systemData,
          data: designJsonForm.designerPropsData.data,
          modal: designJsonForm.designerPropsData.modal,
          isOpenModal,
          openModal,
          modalKey: modalKey,
          isModalCondition,
          modalCode,
          uiModalCode,
          ui,
          modalAction,
          formData: jsonForm.formData,
          datasourceValue: datasourceValue,
          jsonForm: jsonForm,
          designerPropsData: designerPropsData,
        })

        if (ui.ui) {
          objectPath.del(ui, 'ui')
        }
      } else {
        setModalLoadFail()
      }
    } catch (error) {
      if (modalLoadRequestRef.current !== modalLoadRequest) return
      console.error('[Modal] load modal design error', error)
      setModalLoadFail()
    }
    objectPath.set(isUiChanged, 'modal.isOpening', true)
    setIsUiChanged(isUiChanged)
  }, [isOpenModal])

  const patientAssessmentId = useMemo(() => {
    let assessmentKey = objectPath.get(modalAction, 'assessment.id')
    let assessmentLayout = objectPath.get(modalAction, 'assessment.layout')
    let patientAssessmentId = null
    if (!assessmentKey) return patientAssessmentId
    if (assessmentLayout === 'urlParam') {
      patientAssessmentId = objectPath.get(allParams, assessmentKey)
    } else {
      patientAssessmentId =
        objectPath.get(jsonForm.formData, assessmentKey) || null
    }
    return patientAssessmentId
  }, [isOpenModal])

  if (!isOpenModal) return <></>
  if (openModal !== ui.key) return <></>

  const renderModalContent = () => {
    if (modalLoadStatus === 'idle' || modalLoadStatus === 'loading') {
      return <FormLoading />
    }

    if (modalLoadError) {
      return <div className="p-4 text-center text-muted">{modalLoadError}</div>
    }

    if (modalType === 'assessment') {
      return (
        <PatientAssessment
          form={{
            code: modalAction.assessmentCode,
            name: modalAction.assessmentName,
            patientAssessmentId: patientAssessmentId,
          }}
          modal={modal}
          isOpenModal={isOpenModal}
          componentKey={componentKey}
          designerPropsData={designerPropsData}
          closeExternalModal={closeExternalModal}
          onCancelAssessment={onCancelAssessment}
        />
      )
    }

    return (
      <Component
        uischema={modal}
        action={modalAction}
        isOpenModal={isOpenModal}
        modalCode={modalCode}
        modalId={modalId}
        closeExternalModal={closeExternalModal}
        onRefreshTable={onRefreshTable}
        modalDynamicIndex={modalDynamicIndex}
        modalOpenSource={modalOpenSource}
        isModalCondition={isModalCondition}
      />
    )
  }

  if (modalSize === 'fullscreen') {
    return (
      <FullSizeModal
        {...props}
        isOpenModal={isOpenModal}
        closeExternalModal={closeExternalModal}
      >
        {renderModalContent()}
      </FullSizeModal>
    )
  }

  return (
    <NormalModal
      {...props}
      isOpenModal={isOpenModal}
      closeExternalModal={closeExternalModal}
    >
      {renderModalContent()}
    </NormalModal>
  )
}

export default ModalComponent
